import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Dropdown } from './Dropdown';
import { classNames } from '~/utils/classNames';

export type CustomSelectItem = {
  label: ReactNode;
  value: string;
  type?: 'default' | 'danger';
};

export const CustomSelect = ({
  triggerNode,
  items,
  onSelect,
  itemClassName,
  changeTriggerNode = false,
  contentClassName,
  triggerClassName,
  disabled,
}: {
  triggerNode?: ReactNode;
  items: CustomSelectItem[];
  onSelect?: (value: string) => void;
  itemClassName?: string;
  changeTriggerNode?: boolean;
  contentClassName?: string;
  triggerClassName?: string;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<CustomSelectItem | null>(null);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleSelect = (item: CustomSelectItem) => {
    setSelected(item);
    onSelect?.(item.value);
    setOpen(false);
  };

  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <Dropdown
      trigger={
        <button ref={triggerRef} className="flex gap-2 justify-between items-center">
          <span className="text-sm">{selected?.label && changeTriggerNode ? selected.label : triggerNode}</span>
          <span className={classNames('text-xl', open ? 'i-ph:caret-up' : 'i-ph:caret-down')} />
        </button>
      }
      contentClassName={contentClassName}
      triggerClassName={classNames(
        'px-3 py-2 rounded-md bg-transparent text-bolt-ds-textSecondary min-h-6 capitalize',
        triggerClassName,
      )}
      disabled={disabled}
      open={open}
      onOpenChange={toggleOpen}
    >
      {items.map((item, i) => (
        <Dropdown.Item
          key={i}
          onClick={() => handleSelect(item)}
          className={classNames(
            itemClassName,
            item.type === 'danger' ? 'text-bolt-ds-danger' : '',
            selected?.value === item.value ? 'font-semibold' : '',
          )}
        >
          {item.label}
        </Dropdown.Item>
      ))}
    </Dropdown>
  );
};
